import { z } from "zod";

export const supportedAppLocales = ["en", "zh"] as const;
export const supportedLanguages = ["en", "ja", "zh"] as const;
export const supportedCallerLanguages = ["en", "zh"] as const;
export const supportedCalleeLanguages = ["ja"] as const;

export const callModes = ["translator", "autonomous"] as const;
export type CallMode = (typeof callModes)[number];

export const taskOutcomes = [
  "success",
  "partial",
  "failure",
  "voicemail",
  "no_answer",
] as const;
export type TaskOutcome = (typeof taskOutcomes)[number];

export const minimumCallReserveCredits = 120;
export const demoTopUpAmount = 500;
export const defaultRatePerMinute = 36;
export const mobileRatePerMinute = 60;

export function isJapaneseMobileNumber(e164: string): boolean {
  return /^\+81(70|80|90)\d{8}$/.test(e164);
}

export function resolveRatePerMinuteForDestination(e164: string): number {
  return isJapaneseMobileNumber(e164) ? mobileRatePerMinute : defaultRatePerMinute;
}

export const productCredits = {
  "com.nativecall.topup.499": 500,
  "com.nativecall.topup.1199": 1200,
  "com.nativecall.topup.2499": 3000,
} as const;

export type TopUpProductId = keyof typeof productCredits;

export const topUpProductIds = Object.keys(productCredits) as TopUpProductId[];

export function isTopUpProductId(value: string): value is TopUpProductId {
  return value in productCredits;
}

export function creditsForProduct(productId: TopUpProductId): number {
  return productCredits[productId];
}

export type AppLocale = (typeof supportedAppLocales)[number];
export type SupportedLanguage = (typeof supportedLanguages)[number];
export type CallerLanguage = (typeof supportedCallerLanguages)[number];
export type CalleeLanguage = (typeof supportedCalleeLanguages)[number];

export type CallStatus =
  | "idle"
  | "validating"
  | "dialing"
  | "ringing"
  | "connected"
  | "translating"
  | "degraded"
  | "ended"
  | "failed";

export const legStates = [
  "idle",
  "preparing",
  "ready",
  "dialing",
  "ringing",
  "connecting",
  "connected",
  "streaming",
  "ended",
  "failed",
] as const;

export const translationStates = [
  "idle",
  "connecting",
  "active",
  "ended",
  "failed",
] as const;

export const callFailureCodes = [
  "agent_max_duration_exceeded",
  "agent_session_rejected",
  "agent_stalled",
  "mic_permission_denied",
  "browser_leg_timeout",
  "browser_leg_disconnected",
  "pstn_leg_timeout",
  "openai_session_rejected",
  "openai_stream_closed",
  "media_leg_missing",
  "provider_webhook_invalid",
  "provider_call_failed",
  "user_cancelled",
  "split_stream_failed",
  "unknown",
] as const;

export type LegState = (typeof legStates)[number];
export type TranslationState = (typeof translationStates)[number];
export type CallFailureCode = (typeof callFailureCodes)[number];

export const userProfileSchema = z.object({
  id: z.string(),
  name: z.string(),
  appLocale: z.enum(supportedAppLocales),
  defaultCallerLanguage: z.enum(supportedCallerLanguages),
});

export type UserProfile = z.infer<typeof userProfileSchema>;

export const walletSchema = z.object({
  userId: z.string(),
  balanceCredits: z.number().int().nonnegative(),
  reservedCredits: z.number().int().nonnegative(),
  ratePerMinuteCredits: z.number().int().positive(),
});

export type Wallet = z.infer<typeof walletSchema>;

export const callRequestSchema = z.object({
  destinationNumberE164: z
    .string()
    .regex(/^\+[1-9]\d{7,14}$/)
    .refine((value) => value.startsWith("+81"), {
      message: "Only Japanese destination numbers are supported in v1.",
    }),
  callerLanguage: z.enum(supportedCallerLanguages),
  calleeLanguage: z.enum(supportedCalleeLanguages),
});

export type CallRequest = z.infer<typeof callRequestSchema>;

export const transcriptSegmentSchema = z.object({
  id: z.string(),
  callSessionId: z.string(),
  speaker: z.enum(["caller", "callee"]),
  sourceLanguage: z.enum(supportedLanguages),
  sourceText: z.string(),
  translatedLanguage: z.enum(supportedLanguages),
  translatedText: z.string(),
  confidence: z.number().min(0).max(1),
  startedAtMs: z.number().int().nonnegative(),
  endedAtMs: z.number().int().nonnegative(),
});

export type TranscriptSegment = z.infer<typeof transcriptSegmentSchema>;

export const callSessionSchema = z.object({
  id: z.string(),
  userId: z.string(),
  destinationNumber: z.string(),
  callerLanguage: z.enum(supportedCallerLanguages),
  calleeLanguage: z.enum(supportedCalleeLanguages),
  status: z.custom<CallStatus>(),
  callerLegState: z.enum(legStates),
  pstnLegState: z.enum(legStates),
  translationState: z.enum(translationStates),
  degraded: z.boolean(),
  disclosurePlayed: z.boolean(),
  transcript: z.array(transcriptSegmentSchema),
  createdAt: z.string(),
  startedAt: z.string().nullable(),
  endedAt: z.string().nullable(),
  estimatedRatePerMinuteCredits: z.number().int().positive(),
  usedCredits: z.number().int().nonnegative(),
  failureCode: z.enum(callFailureCodes).nullable(),
  failureReason: z.string().nullable(),
  disconnectReason: z.string().nullable(),
  providerCallSid: z.string().nullable(),
  providerCallStatus: z.string().nullable(),
  mode: z.enum(callModes).default("translator"),
  taskGoal: z.string().nullable().default(null),
  taskOutcome: z.enum(taskOutcomes).nullable().default(null),
  taskSummary: z.string().nullable().default(null),
  callbackNumber: z.string().nullable().default(null),
});

export type CallSession = z.infer<typeof callSessionSchema>;

export const signupRequestSchema = z.object({
  name: z.string().min(1).max(80),
  appLocale: z.enum(supportedAppLocales),
  defaultCallerLanguage: z.enum(supportedCallerLanguages),
  acceptedLegal: z.literal(true),
});

export type SignupRequest = z.infer<typeof signupRequestSchema>;

export const appleSignInRequestSchema = z.object({
  appleUserId: z.string().min(1).max(256),
  fullName: z.string().min(1).max(80).optional(),
  appLocale: z.enum(supportedAppLocales),
  defaultCallerLanguage: z.enum(supportedCallerLanguages),
  acceptedLegal: z.literal(true),
});

export type AppleSignInRequest = z.infer<typeof appleSignInRequestSchema>;

export const signupResponseSchema = z.object({
  user: userProfileSchema,
  wallet: walletSchema,
  billingMode: z.literal("manual_prepaid"),
});

export type SignupResponse = z.infer<typeof signupResponseSchema>;

export const walletTopUpRequestSchema = z.object({
  userId: z.string(),
  amountCredits: z.number().int().positive(),
});

export type WalletTopUpRequest = z.infer<typeof walletTopUpRequestSchema>;

export const purchaseFulfillRequestSchema = z.object({
  userId: z.string(),
  productId: z.string().refine(isTopUpProductId, {
    message: "Unknown product id.",
  }),
  transactionId: z.string().min(1),
});

export type PurchaseFulfillRequest = z.infer<typeof purchaseFulfillRequestSchema>;

export const purchaseFulfillResponseSchema = z.object({
  wallet: walletSchema,
  creditsGranted: z.number().int().nonnegative(),
  alreadyFulfilled: z.boolean(),
});

export type PurchaseFulfillResponse = z.infer<
  typeof purchaseFulfillResponseSchema
>;

export const createCallSessionRequestSchema = z
  .object({
    userId: z.string(),
    destinationNumberE164: z.string(),
    callerLanguage: z.enum(supportedCallerLanguages),
    calleeLanguage: z.enum(supportedCalleeLanguages),
    mode: z.enum(callModes).optional().default("translator"),
    taskGoal: z.string().trim().min(10).max(500).optional(),
    callbackNumberE164: z
      .string()
      .regex(/^\+[1-9]\d{7,14}$/)
      .optional(),
    stack: z.enum(["realtime", "split"]).optional(),
    record: z.boolean().optional(),
  })
  .superRefine((value, ctx) => {
    if (value.mode === "autonomous" && !value.taskGoal) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["taskGoal"],
        message: "taskGoal is required when mode is autonomous.",
      });
    }
  });

export type CreateCallSessionRequest = z.infer<
  typeof createCallSessionRequestSchema
>;

export const startCallSessionRequestSchema = z.object({
  userId: z.string(),
  confirmedConsent: z.literal(true),
  confirmedAcceptableUse: z.literal(true),
  stack: z.enum(["realtime", "split"]).optional(),
  record: z.boolean().optional(),
  mock: z.boolean().optional(),
});

export const recordingStreamIds = [
  "caller-original",
  "callee-original",
  "translation-to-caller",
  "translation-to-callee",
  "caller-perspective",
  "callee-perspective",
] as const;

export type RecordingStreamId = (typeof recordingStreamIds)[number];

export const callRecordingSchema = z.object({
  streamId: z.enum(recordingStreamIds),
  url: z.string().url(),
  sizeBytes: z.number().int().nonnegative(),
  contentType: z.string(),
  label: z.string(),
  channels: z.number().int().min(1).max(2),
});

export type CallRecording = z.infer<typeof callRecordingSchema>;

export const callRecordingsResponseSchema = z.object({
  callSessionId: z.string(),
  recordings: z.array(callRecordingSchema),
  expiresAt: z.string(),
});

export type CallRecordingsResponse = z.infer<
  typeof callRecordingsResponseSchema
>;

export type StartCallSessionRequest = z.infer<
  typeof startCallSessionRequestSchema
>;

export type RealtimeEvent =
  | {
      type:
        | "call.ringing"
        | "call.connected"
        | "call.degraded"
        | "call.ended"
        | "call.updated";
      callSession: CallSession;
    }
  | {
      type: "call.translation.segment";
      callSessionId: string;
      segment: TranscriptSegment;
    }
  | {
      type: "call.translation.low_confidence";
      callSessionId: string;
      segment: TranscriptSegment;
      message: string;
    }
  | {
      type: "call.translation.segment.partial";
      callSessionId: string;
      speaker: "caller" | "callee";
      sourceLanguage: SupportedLanguage;
      sourceText: string;
      startedAtMs: number;
    }
  | {
      type: "wallet.updated";
      wallet: Wallet;
    }
  | {
      type: "call.agent.status";
      callSessionId: string;
      status: "dialing" | "talking" | "wrapping_up" | "completed";
    }
  | {
      type: "call.agent.outcome";
      callSessionId: string;
      outcome: TaskOutcome;
      summary: string;
    };
